import { FlatList } from "react-native";
import { Href } from "expo-router";
import { AlertTriangleIcon, ChevronRightIcon } from "lucide-react-native";

import { ScreenLayout } from "@/blocks/ScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnLink } from "@/components/VnLink";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { useListWorkRequirementsQuery } from "@/data/queries/workRequirementsQueries";
import {
  getNextWorkRequirementStepLink,
  WorkRequirementType,
} from "@/domain/workRequirements";
import { I18nKeyType } from "@/i18n";
import { formatLongDate } from "@/utils/date";

const PROGRESS_I18N_KEYS: Record<WorkRequirementType["progress"], I18nKeyType> =
  {
    ACTION_NEEDED: "native:workRequirements.progress_ACTION_NEEDED",
    UP_TO_DATE: "native:workRequirements.progress_UP_TO_DATE",
    COMPLETED: "native:workRequirements.progress_COMPLETED",
  };

// A recheck left unfinished resumes the screening; otherwise the tile opens
// the household's next steps.
const getTileLink = (workRequirement: WorkRequirementType): Href =>
  workRequirement.screeningStatus === "COMPLETED"
    ? {
        pathname: "/(authed)/work-requirements/[id]",
        params: { id: workRequirement.id },
      }
    : getNextWorkRequirementStepLink({
        workRequirementId: workRequirement.id,
        step: workRequirement.screeningLastStep,
        members: workRequirement.members,
      });

export const WorkRequirementsListScreen = () => {
  const {
    data: workRequirements,
    isPending,
    isError,
  } = useListWorkRequirementsQuery();
  const screenedWorkRequirements = (workRequirements ?? []).filter(
    workRequirement => workRequirement.screenedAt !== null,
  );

  if (isPending) {
    return (
      <ScreenLayout>
        <VnBox className="flex-1 items-center justify-center">
          <VnLoadingSpinner />
        </VnBox>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout variant="LIST">
      <VnBox className="flex-1 gap-5 pt-4">
        <VnText
          variant="headerTwo"
          i18nKey="native:workRequirements.medicaidWorkRequirements"
        />

        <FlatList
          data={screenedWorkRequirements}
          keyExtractor={item => item.id}
          contentContainerClassName="gap-4 pb-8"
          renderItem={({ item }) => {
            const isActionNeeded =
              item.progress === "ACTION_NEEDED" ||
              item.screeningStatus !== "COMPLETED";

            return (
              <VnLink href={getTileLink(item)} asChild>
                <VnCard className="flex-row items-center justify-between gap-4">
                  <VnBox className="flex-1 gap-2">
                    <VnText
                      variant="lgMedium"
                      i18nKey={
                        isActionNeeded
                          ? PROGRESS_I18N_KEYS.ACTION_NEEDED
                          : PROGRESS_I18N_KEYS[item.progress]
                      }
                    />
                    <VnText
                      variant="md"
                      className="text-muted-foreground"
                      i18nKey="native:workRequirements.due"
                      i18nOptions={{
                        date: formatLongDate(item.workRequirementDate),
                      }}
                    />
                  </VnBox>
                  {isActionNeeded && (
                    <VnBox className="rounded-full bg-black/5 p-2">
                      <VnIcon as={AlertTriangleIcon} size={18} />
                    </VnBox>
                  )}
                  <VnIcon as={ChevronRightIcon} />
                </VnCard>
              </VnLink>
            );
          }}
          ListEmptyComponent={
            <VnText
              variant="md"
              className="text-muted-foreground text-center"
              i18nKey={
                isError
                  ? "common:errors.unknown"
                  : "native:workRequirements.noWorkRequirements"
              }
            />
          }
        />
      </VnBox>
    </ScreenLayout>
  );
};
